import { useCallback, useEffect, useState } from 'react';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';

const emptyRate = { from_currency: 'USD', to_currency: 'EUR', rate: '' };
const emptyConvert = { amount: '100', from_currency: 'USD', to_currency: 'EUR' };

export default function CurrencyPage() {
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rateForm, setRateForm] = useState(emptyRate);
  const [convertForm, setConvertForm] = useState(emptyConvert);
  const [convertResult, setConvertResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRates = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await shopApi.listExchangeRates(token, selectedShopId);
      setRates(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const handleCreateRate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.createExchangeRate(token, selectedShopId, {
        ...rateForm,
        rate: Number(rateForm.rate),
      });
      setModalOpen(false);
      setRateForm(emptyRate);
      await loadRates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Delete this exchange rate?')) return;
    setError(null);
    try {
      const token = await getToken();
      await shopApi.deleteExchangeRate(token, selectedShopId, rateId);
      await loadRates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleConvert = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await shopApi.convertCurrency(token, selectedShopId, {
        amount: Number(convertForm.amount),
        from_currency: convertForm.from_currency,
        to_currency: convertForm.to_currency,
      });
      setConvertResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Currency"
        description="Exchange rates used when checkout currency differs from catalog prices."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Add rate
          </button>
        }
      />

      <ErrorBanner message={error} />

      {loading ? (
        <LoadingState />
      ) : rates.length === 0 ? (
        <EmptyState message="No exchange rates configured yet." />
      ) : (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Rate</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rates.map((rate) => (
                <tr key={rate.id}>
                  <td>{rate.from_currency}</td>
                  <td>{rate.to_currency}</td>
                  <td>{rate.rate}</td>
                  <td>{new Date(rate.updated_at).toLocaleString()}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(rate.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Convert preview</h3>
          <form className="form-grid" onSubmit={handleConvert}>
            <div className="form-grid two-col">
              <div className="form-field">
                <label htmlFor="convert-amount">Amount</label>
                <input
                  id="convert-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={convertForm.amount}
                  onChange={(e) => setConvertForm((p) => ({ ...p, amount: e.target.value }))}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="convert-from">From</label>
                <input
                  id="convert-from"
                  maxLength={3}
                  value={convertForm.from_currency}
                  onChange={(e) => setConvertForm((p) => ({ ...p, from_currency: e.target.value.toUpperCase() }))}
                  required
                />
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="convert-to">To</label>
              <input
                id="convert-to"
                maxLength={3}
                value={convertForm.to_currency}
                onChange={(e) => setConvertForm((p) => ({ ...p, to_currency: e.target.value.toUpperCase() }))}
                required
              />
            </div>
            <button type="submit" className="btn btn-secondary" disabled={saving}>
              {saving ? 'Converting…' : 'Preview conversion'}
            </button>
          </form>
          {convertResult && (
            <p className="dns-record-hint" style={{ marginTop: '1rem' }}>
              {convertResult.amount} {convertResult.from_currency} ={' '}
              <strong>
                {convertResult.converted_amount} {convertResult.to_currency}
              </strong>{' '}
              (rate {convertResult.rate})
            </p>
          )}
        </div>
      </div>

      <Modal
        title="Add exchange rate"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="rate-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Add rate'}
            </button>
          </>
        }
      >
        <form id="rate-form" className="form-grid" onSubmit={handleCreateRate}>
          <div className="form-grid two-col">
            <div className="form-field">
              <label htmlFor="rate-from">From</label>
              <input
                id="rate-from"
                maxLength={3}
                value={rateForm.from_currency}
                onChange={(e) => setRateForm((p) => ({ ...p, from_currency: e.target.value.toUpperCase() }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="rate-to">To</label>
              <input
                id="rate-to"
                maxLength={3}
                value={rateForm.to_currency}
                onChange={(e) => setRateForm((p) => ({ ...p, to_currency: e.target.value.toUpperCase() }))}
                required
              />
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="rate-value">Rate</label>
            <input
              id="rate-value"
              type="number"
              min="0"
              step="0.000001"
              value={rateForm.rate}
              onChange={(e) => setRateForm((p) => ({ ...p, rate: e.target.value }))}
              required
            />
          </div>
        </form>
      </Modal>
    </RequireShop>
  );
}
